import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { DBInvoice } from "@/lib/db-types";

export interface InvoiceWithProject extends DBInvoice {
  project?: {
    id: string;
    name: string;
    project_code: string | null;
    customer_id: string | null;
  };
  customer?: {
    id: string;
    name: string;
  };
}

/**
 * VIB-1253: kunde-visningsnavn med fallback-kjede. Prosjekt-kunde (ekte
 * kobling via customers-tabellen) har prioritet; deretter customer_name
 * hentet fra PO ved sync (dekker PO-fakturaer uten prosjekt-kobling);
 * "-" kun når begge mangler.
 */
export function resolveInvoiceCustomerLabel(
  inv: Pick<InvoiceWithProject, "customer" | "customer_name">,
): string {
  return inv.customer?.name ?? inv.customer_name ?? "-";
}

export function useInvoices() {
  return useQuery({
    queryKey: ["invoices"],
    queryFn: async (): Promise<InvoiceWithProject[]> => {
      // Paginate invoices — table passed 1000 rows, un-paged select silently
      // hid older invoices from the list. See VIB-1249.
      const invoicesData = await fetchAllPaginated<InvoiceWithProject>(
        "invoices",
        "*, project:projects(id, name, project_code, customer_id)",
      );

      // fetchAllPaginated orders by id for stable paging; re-sort for display
      const invoices = invoicesData.sort((a, b) =>
        (b.created_at ?? "").localeCompare(a.created_at ?? "")
      );
      const customerIds = new Set<string>();

      invoices.forEach(inv => {
        // VIB-3030: kunden fakturaen gikk til, ellers prosjektets kunde.
        const id = inv.customer_id ?? inv.project?.customer_id;
        if (id) customerIds.add(id);
      });

      if (customerIds.size > 0) {
        const { data: customerData } = await supabase
          .from("customers")
          .select("id, name")
          .in("id", Array.from(customerIds));

        const customerMap = new Map((customerData ?? []).map(c => [c.id, c]));

        invoices.forEach(inv => {
          const id = inv.customer_id ?? inv.project?.customer_id;
          if (id && customerMap.has(id)) {
            inv.customer = customerMap.get(id);
          }
        });
      }

      return invoices;
    },
    staleTime: 5 * 60 * 1000,
  });
}
